'use client';

/**
 * MentionsList - Renders a collection of Mention Markets.
 * Handles different layouts for mobile/desktop and manages loading/empty states.
 */

import { Box } from '@mui/material';
import { format } from 'date-fns';

import { EmptyState } from '@/components';
import { MentionsSkeleton } from '@/components/skeletons/mentions/MentionsSkeleton';
import type { EventResponse } from '@/types';
import { formatCurrency, normalizeMarketQuestion } from '@/utils';
import { DesktopMentionItem } from './DesktopMentionItem';
import { MobileMentionItem } from './MobileMentionItem';

interface MentionsListProps {
  /** The list of event data to display */
  events: EventResponse[];
  /** Loading state of the query */
  isLoading: boolean;
  /** Responsive trigger for switchable layouts */
  isMobile: boolean;
  /** Callback for when a user wants to view market details */
  onTradeClick: (eventId: string) => void;
}

export const MentionsList = ({ events, isLoading, isMobile, onTradeClick }: MentionsListProps) => {
  // 1. Initial Loading State (No data yet)
  if (isLoading && events.length === 0) {
    return <MentionsSkeleton />;
  }

  // 2. Empty State (Finished loading, no results)
  if (events.length === 0 && !isLoading) {
    return (
      <EmptyState
        title="No mention events found"
        description="Check back later for new events where you can predict mentions."
      />
    );
  }

  /**
   * Transforms raw Event data into formatted props for the display items.
   */
  const renderMentionItem = (event: EventResponse) => {
    // 3. Date Formatting (e.g., "12", "DEC", "Fri, 6:00 PM")
    const startTime = event.startTime ? new Date(event.startTime) : null;
    const dateDay = startTime ? format(startTime, 'd') : 'TBD';
    const dateMonth = startTime ? format(startTime, 'MMM') : null;
    const dateTime = startTime ? format(startTime, 'eee, h:mm a') : null;

    // 4. Content Extraction
    // We show the first 2 markets as quick-bet options on the card
    const markets = event.markets || [];
    const options = markets.slice(0, 2).map((m) => normalizeMarketQuestion(m));
    const moreOptionsCount = Math.max(0, markets.length - 2);
    const formattedVolume = formatCurrency(event.volume || 0);

    const commonProps = {
      event,
      dateDay,
      dateMonth,
      dateTime,
      options,
      moreOptionsCount,
      formattedVolume,
      onTradeClick,
    };

    // 5. Responsive Switch
    if (isMobile) {
      return <MobileMentionItem key={event.id} {...commonProps} />;
    }
    return <DesktopMentionItem key={event.id} {...commonProps} />;
  };

  const renderedEvents = events.map(renderMentionItem);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {renderedEvents}

      {/* 6. Incremental Loading (Show a single skeleton at the bottom if more data is coming) */}
      {isLoading && events.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <MentionsSkeleton count={1} />
        </Box>
      )}
    </Box>
  );
};
